import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Edit, Trash2, Package, AlertTriangle, Mail } from "lucide-react";

const EMPTY = { name: "", vendor_id: "", quantity: "", unit: "units", purchase_price: "", selling_price: "", expiry_date: "", batch_number: "", category: "medicine", reorder_level: "10" };

export default function Inventory() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showLowStock, setShowLowStock] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/inventory"), api.get("/vendors")]).then(([inv, v]) => {
      setItems(inv.data); setVendors(v.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const fetchItems = async () => {
    const { data } = await api.get("/inventory");
    setItems(data);
  };

  const openAdd = () => { setForm(EMPTY); setEditItem(null); setShowModal(true); };
  const openEdit = (item) => { setForm({...item}); setEditItem(item); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity),
        purchase_price: parseFloat(form.purchase_price),
        selling_price: parseFloat(form.selling_price),
        reorder_level: parseInt(form.reorder_level),
      };
      if (editItem) {
        await api.put(`/inventory/${editItem.id}`, payload);
        toast({ title: "Item updated" });
      } else {
        await api.post("/inventory", payload);
        toast({ title: "Item added" });
      }
      setShowModal(false); fetchItems();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/inventory/${id}`);
      toast({ title: "Item deleted" });
      fetchItems();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const getStockStatus = (item) => {
    if (item.quantity <= 0) return { label: "Out of Stock", cls: "bg-rose-100 text-rose-800" };
    if (item.quantity <= item.reorder_level) return { label: "Low Stock", cls: "bg-amber-100 text-amber-800" };
    return { label: "In Stock", cls: "bg-emerald-100 text-emerald-800" };
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const diff = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff > 0;
  };

  const isExpired = (date) => date && new Date(date) < new Date();

  let filtered = items.filter(i => i.name?.toLowerCase().includes(search.toLowerCase()));
  if (showLowStock) filtered = filtered.filter(i => i.quantity <= i.reorder_level);

  const lowStockCount = items.filter(i => i.quantity <= i.reorder_level).length;
  const [alerting, setAlerting] = useState(false);

  const alertAdmin = async () => {
    setAlerting(true);
    try {
      const { data } = await api.post("/inventory/alert-admin");
      toast({
        title: data.mode === "real" ? "Alert email sent" : "Demo alert queued",
        description: `${data.low_stock_count} low-stock item(s) · sent to ${data.recipient}${data.mode === "mocked" ? " (MOCKED)" : ""}`
      });
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setAlerting(false); }
  };
  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || "-";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("inventory")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage medicine stock and inventory</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-inventory-btn">
          <Plus size={16} /> Add Item
        </Button>
      </div>

      {/* Alert Banner */}
      {lowStockCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="low-stock-alert">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 font-medium">{lowStockCount} item(s) are running low on stock</p>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100 gap-1.5" onClick={alertAdmin} disabled={alerting} data-testid="alert-admin-btn">
              <Mail size={13} /> {alerting ? "Sending..." : "Notify Admin"}
            </Button>
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => setShowLowStock(!showLowStock)}>
              {showLowStock ? "Show All" : "View Low Stock"}
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search inventory..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="inventory-search" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Item</TableHead>
              <TableHead className="font-semibold text-slate-700">Vendor</TableHead>
              <TableHead className="font-semibold text-slate-700">Quantity</TableHead>
              <TableHead className="font-semibold text-slate-700">Price (₹)</TableHead>
              <TableHead className="font-semibold text-slate-700">Expiry</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                <Package size={32} className="mx-auto mb-2 opacity-30" /><p>No items found</p>
              </TableCell></TableRow>
            ) : filtered.map(item => {
              const stock = getStockStatus(item);
              return (
                <TableRow key={item.id} className={`table-row-hover ${isExpired(item.expiry_date) ? "bg-rose-50/30" : ""}`} data-testid={`inv-row-${item.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900 text-sm">{item.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{item.category} • {item.batch_number || "—"}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{getVendorName(item.vendor_id)}</TableCell>
                  <TableCell>
                    <span className="font-medium text-slate-800">{item.quantity}</span>
                    <span className="text-xs text-slate-500 ml-1">{item.unit}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">₹{item.selling_price}</TableCell>
                  <TableCell>
                    {item.expiry_date ? (
                      <span className={`text-xs ${isExpired(item.expiry_date) ? "text-rose-600 font-medium" : isExpiringSoon(item.expiry_date) ? "text-amber-600 font-medium" : "text-slate-600"}`}>
                        {item.expiry_date}
                        {isExpired(item.expiry_date) && " (Expired)"}
                        {isExpiringSoon(item.expiry_date) && " (Soon)"}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${stock.cls}`}>{stock.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(item)} data-testid={`edit-inv-${item.id}`}>
                        <Edit size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(item.id)} data-testid={`delete-inv-${item.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans">{editItem ? "Edit Item" : "Add Inventory Item"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Item Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="inv-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Vendor</Label>
                <select value={form.vendor_id} onChange={e => setForm({...form, vendor_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Category</Label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize">
                  {["medicine","equipment","consumables","laboratory"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Quantity</Label>
                <Input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="mt-1 border-slate-200" required data-testid="inv-qty-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Unit</Label>
                <Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="units, tablets, bottles" className="mt-1 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Purchase Price (₹)</Label>
                <Input type="number" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: e.target.value})} className="mt-1 border-slate-200" required />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Selling Price (₹)</Label>
                <Input type="number" value={form.selling_price} onChange={e => setForm({...form, selling_price: e.target.value})} className="mt-1 border-slate-200" required />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Expiry Date</Label>
                <Input type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} className="mt-1 border-slate-200" data-testid="inv-expiry-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Reorder Level</Label>
                <Input type="number" value={form.reorder_level} onChange={e => setForm({...form, reorder_level: e.target.value})} className="mt-1 border-slate-200" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-inv-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
